// ADR 30.9.26i §2.2/§2.3 — the learner's own hand on the gate.
//
// A grant goes to the server first and a withdrawal goes local first. The
// merge is `local OR server`, and these two orders are the only ones under
// which neither failure leaves a setting that silently reverses:
//
// - a withdrawal must hold with the network down — local-first does that, and
//   a failed PUT cannot undo it, because the merge only ever tightens;
// - a grant written locally before a PUT that then failed would be re-denied by
//   the next load's reconcile (`false OR true`) without a word. Server-first
//   makes that failure visible now, on the row the learner just touched.
//
// Nothing is ever reloaded, and nothing from a denied period is replayed: a
// mid-session PostHog calls none of `main.tsx`'s launch flushes.
//
// Imports `lifecycle.ts`, never `index.ts`, so the two have no cycle.

import { putPrivacy } from '../api/settings'
import { DEFAULT_CONSENT, readLocalConsent, writeLocalConsent } from './consent'
import { signedIn, signedOut } from './identity'
import {
  bumpGrantEpoch,
  CONSENT_FIELD,
  CONSENT_SDKS,
  construct,
  reportInitOutcomes,
  sdkState,
  withdraw,
  type ConsentSdk,
} from './lifecycle'

/**
 * `granted` — in force now (also when a build with no credential constructed
 * nothing: the choice is recorded, and there is nothing to be late for);
 * `next_load` — recorded, but this page already tore the SDK down once, and a
 * second `sentry.init` / a PostHog `opt_in_capturing()` in one page is not a
 * path worth having; `failed` — the server refused, nothing changed.
 */
export type GrantOutcome = 'granted' | 'next_load' | 'failed'

/**
 * ADR 30.9.26l §1 — both callers are only ever reached from a person's choice
 * (the consent strip or an `/account` row), so every write here is an answer.
 */
function writeField(sdk: ConsentSdk, optedOut: boolean): void {
  writeLocalConsent({ ...readLocalConsent(), [CONSENT_FIELD[sdk]]: optedOut, answered: true })
}

/**
 * absent → constructing → live, server first. `userId` comes from the caller
 * (`telemetry/` has no session context), and joins PostHog's identity only
 * *after* the attach: the identity sink checks `attached()` synchronously.
 *
 * ADR 30.9.26l §2 — a `null` user is a signed-out visitor, who has no account
 * row to write, so the `PUT` is skipped and the grant proceeds as if the server
 * had agreed. Server-first exists so the next reconcile cannot silently re-deny
 * a grant, and with no session there is no reconcile until sign-in — which
 * then applies the row most-restrictive-wins, as intended. A `live` (or
 * `constructing`) SDK records the grant and constructs nothing: the one
 * already attached, or attaching, is the one that counts.
 */
export async function grantConsent(sdk: ConsentSdk, userId: string | null): Promise<GrantOutcome> {
  if (userId !== null) {
    try {
      await putPrivacy({ [CONSENT_FIELD[sdk]]: false })
    } catch {
      return 'failed'
    }
  }
  writeField(sdk, false)
  bumpGrantEpoch(sdk)
  const state = sdkState(sdk)
  if (state === 'closed') return 'next_load'
  if (state !== 'absent') return 'granted'
  // 8.9.26b §3 — the one outcome reported exactly as `initTelemetry` reports two.
  reportInitOutcomes([sdk], await Promise.allSettled([construct(sdk)]))
  if (sdk === 'analytics' && userId !== null && sdkState(sdk) === 'live') signedIn(userId, false)
  return 'granted'
}

/**
 * live → closed, local first and synchronously — the record and the teardown
 * land before the PUT is even sent. Resolves `false` when the sync failed; the
 * withdrawal stands either way.
 */
export async function withdrawConsent(sdk: ConsentSdk): Promise<boolean> {
  writeField(sdk, true)
  try {
    withdraw(sdk)
  } catch (error) {
    console.warn('telemetry teardown failed', error)
  }
  try {
    await putPrivacy({ [CONSENT_FIELD[sdk]]: true })
    return true
  } catch {
    return false
  }
}

/**
 * ADR 30.9.26i §3 step 2 — the account is gone, so this browser is a
 * stranger's again: forget the person while PostHog is still attached (the
 * sink skips `reset` once nothing is), tear both SDKs down locally with no PUT
 * (the row is gone too), and put the record back to the denied default.
 */
export function forgetConsent(): void {
  signedOut()
  for (const sdk of CONSENT_SDKS) {
    try {
      withdraw(sdk)
    } catch (error) {
      console.warn('telemetry teardown failed', error)
    }
  }
  writeLocalConsent(DEFAULT_CONSENT)
}
